import { create } from 'zustand';
import type { PviGraphWithSpans } from '../editor/pviTypes';
import type {
  SimulationInput,
  SimulationInputValues,
  SimulationResult,
  ExecutionStep,
  SimNodeStatus,
  BalanceMap,
} from '../editor/simulationEngine';
import { deriveInputs, runSimulation } from '../editor/simulationEngine';

export type SimMode = 'view' | 'simulation';
export type PlaybackMode = 'auto' | 'step';

interface SimulationState {
  /** Current mode of the PVI panel. */
  mode: SimMode;
  /** Whether simulation is currently playing. */
  playing: boolean;
  /** Auto vs step mode. */
  playbackMode: PlaybackMode;
  /** Speed: ms between steps in auto mode (50–2000). */
  speed: number;
  /** Auto-generated inputs from the graph. */
  inputs: SimulationInput[];
  /** Current input values. */
  inputValues: SimulationInputValues;
  /** Full simulation result (null before first run). */
  result: SimulationResult | null;
  /** Current step index during playback (-1 = not started). */
  currentStep: number;
  /** Per-node runtime status for graph overlay. */
  nodeStatuses: Record<number, SimNodeStatus>;
  /** Active edge IDs being animated in current step. */
  activeEdgeIds: number[];
  /** Current balances at the playback cursor. */
  currentBalances: BalanceMap;

  // Actions
  setMode: (mode: SimMode) => void;
  initFromGraph: (graph: PviGraphWithSpans) => void;
  setInputValue: (name: string, value: string) => void;
  run: () => void;
  stepForward: () => void;
  stepBack: () => void;
  jumpToStep: (index: number) => void;
  play: () => void;
  pause: () => void;
  reset: () => void;
  setSpeed: (speed: number) => void;
  setPlaybackMode: (mode: PlaybackMode) => void;
}

/** Reference to the current graph for simulation execution. */
let _currentGraph: PviGraphWithSpans | null = null;

function applyStep(state: SimulationState, step: ExecutionStep): Partial<SimulationState> {
  const nodeStatuses = { ...state.nodeStatuses };

  // Mark all previous nodes as completed
  if (state.result) {
    for (let i = 0; i < step.index; i++) {
      const prevStep = state.result.steps[i];
      nodeStatuses[prevStep.nodeId] = prevStep.status === 'error' ? 'failed' : 'completed';
    }
  }

  // Mark current node as active
  nodeStatuses[step.nodeId] = 'active';

  return {
    currentStep: step.index,
    nodeStatuses,
    activeEdgeIds: step.edgeIds,
    currentBalances: step.balancesAfter,
  };
}

function buildStatusesUpToStep(result: SimulationResult, stepIdx: number): Record<number, SimNodeStatus> {
  const statuses: Record<number, SimNodeStatus> = {};
  for (let i = 0; i <= stepIdx && i < result.steps.length; i++) {
    const s = result.steps[i];
    statuses[s.nodeId] = i === stepIdx ? 'active' : (s.status === 'error' ? 'failed' : 'completed');
  }
  return statuses;
}

export const useSimulationStore = create<SimulationState>((set, get) => ({
  mode: 'view',
  playing: false,
  playbackMode: 'auto',
  speed: 800,
  inputs: [],
  inputValues: {},
  result: null,
  currentStep: -1,
  nodeStatuses: {},
  activeEdgeIds: [],
  currentBalances: {},

  setMode: (mode) => {
    if (mode === 'view') {
      // Reset simulation state when switching back to view
      set({
        mode,
        playing: false,
        result: null,
        currentStep: -1,
        nodeStatuses: {},
        activeEdgeIds: [],
        currentBalances: {},
      });
    } else {
      set({ mode });
    }
  },

  initFromGraph: (graph) => {
    _currentGraph = graph;
    const inputs = deriveInputs(graph);
    const inputValues: SimulationInputValues = {};
    for (const inp of inputs) {
      inputValues[inp.name] = inp.defaultValue;
    }
    set({
      inputs,
      inputValues,
      result: null,
      currentStep: -1,
      nodeStatuses: {},
      activeEdgeIds: [],
      currentBalances: {},
      playing: false,
    });
  },

  setInputValue: (name, value) => {
    set(s => ({
      inputValues: { ...s.inputValues, [name]: value },
    }));
  },

  run: () => {
    const { inputValues } = get();
    if (!_currentGraph) return;

    const result = runSimulation(_currentGraph, inputValues);

    // Initialize all nodes as idle
    const nodeStatuses: Record<number, SimNodeStatus> = {};
    for (const n of _currentGraph.nodes) {
      nodeStatuses[n.id] = 'idle';
    }

    set({
      result,
      currentStep: -1,
      nodeStatuses,
      activeEdgeIds: [],
      currentBalances: result.steps.length > 0
        ? result.steps[0].balancesBefore
        : {},
      playing: false,
    });
  },

  stepForward: () => {
    const { result, currentStep } = get();
    if (!result || currentStep >= result.steps.length - 1) return;

    const nextStep = result.steps[currentStep + 1];
    set(s => ({
      ...applyStep(s, nextStep),
    }));
  },

  stepBack: () => {
    const { result, currentStep } = get();
    if (!result || currentStep <= 0) {
      // Go back to pre-simulation state
      if (result && currentStep === 0) {
        const nodeStatuses: Record<number, SimNodeStatus> = {};
        if (_currentGraph) {
          for (const n of _currentGraph.nodes) nodeStatuses[n.id] = 'idle';
        }
        set({
          currentStep: -1,
          nodeStatuses,
          activeEdgeIds: [],
          currentBalances: result.steps[0]?.balancesBefore ?? {},
        });
      }
      return;
    }

    const prevIdx = currentStep - 1;
    const prevStep = result.steps[prevIdx];
    set({
      currentStep: prevIdx,
      nodeStatuses: buildStatusesUpToStep(result, prevIdx),
      activeEdgeIds: prevStep.edgeIds,
      currentBalances: prevStep.balancesAfter,
    });
  },

  jumpToStep: (index) => {
    const { result } = get();
    if (!result || index < 0 || index >= result.steps.length) return;

    const step = result.steps[index];
    set({
      currentStep: index,
      nodeStatuses: buildStatusesUpToStep(result, index),
      activeEdgeIds: step.edgeIds,
      currentBalances: step.balancesAfter,
      playing: false,
    });
  },

  play: () => set({ playing: true }),
  pause: () => set({ playing: false }),

  reset: () => {
    const nodeStatuses: Record<number, SimNodeStatus> = {};
    if (_currentGraph) {
      for (const n of _currentGraph.nodes) nodeStatuses[n.id] = 'idle';
    }
    set({
      result: null,
      currentStep: -1,
      nodeStatuses,
      activeEdgeIds: [],
      currentBalances: {},
      playing: false,
    });
  },

  setSpeed: (speed) => set({ speed: Math.max(50, Math.min(2000, speed)) }),
  setPlaybackMode: (mode) => set({ playbackMode: mode }),
}));
